/**
 * La imagen del encabezado, en el editor de plantillas (fase 2): subir una foto o elegir una de las ya subidas, y el
 * link queda puesto solo. Antes había que subirla a otro lado y pegar el link a mano.
 *
 * El link a mano sigue (lo arma `CampoImagen` debajo): una marca puede tener sus fotos en su web.
 */

import { useRef } from "react";
import type { ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { Check, ImagePlus, Loader2 } from "lucide-react";
import { useImagenesOficial } from "../hooks/useImagenesOficial";
import { ACEPTA_IMAGEN, avisoDeMedidas } from "../logic/imagenes";

/** Cuántas se muestran para elegir; las demás, en la página «Imágenes». */
const MAX_A_LA_VISTA = 8;

export function ElegirImagen({ url, onElegir }: { url: string; onElegir: (url: string) => void }) {
  const { lista, cargando, subiendo, subir, errorSubida } = useImagenesOficial();
  const entrada = useRef<HTMLInputElement>(null);
  const conLink = (lista?.imagenes || []).filter((i) => i.url);
  const elegida = conLink.find((i) => i.url === url) || null;
  const aviso = elegida ? avisoDeMedidas(elegida) : null;

  const alElegirArchivo = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const r = await subir(f);
    if (r?.imagen.url) onElegir(r.imagen.url);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 flex-wrap">
        <input ref={entrada} type="file" accept={ACEPTA_IMAGEN} className="hidden" aria-label="Elegir una imagen para subir" onChange={(e) => void alElegirArchivo(e)} />
        <button type="button" onClick={() => entrada.current?.click()} disabled={subiendo || (lista !== null && !lista.conLink)}
          className="inline-flex items-center gap-1.5 rounded-xl border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-100 disabled:opacity-60">
          {subiendo ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
          {subiendo ? "Subiendo…" : "Subir una imagen"}
        </button>
        <span className="text-[11px] text-slate-400">JPG o PNG de hasta 5 MB. El link se pone solo.</span>
      </div>
      {lista && !lista.conLink && (
        <p className="text-[11px] text-amber-700">El servidor no tiene su dirección pública: por ahora pegá el link de una imagen de tu web.</p>
      )}
      {errorSubida && <p className="text-xs text-red-700" role="alert">{errorSubida}</p>}
      {cargando && !lista ? (
        <p className="text-[11px] text-slate-400">Buscando tus imágenes…</p>
      ) : conLink.length > 0 ? (
        <div>
          <p className="text-[11px] font-semibold text-slate-500 mb-1">O elegí una ya subida</p>
          <ul className="flex gap-2 flex-wrap" aria-label="Imágenes ya subidas">
            {conLink.slice(0, MAX_A_LA_VISTA).map((i) => {
              const esta = i.url === url;
              return (
                <li key={i.id}>
                  <button type="button" onClick={() => onElegir(String(i.url))} aria-pressed={esta} title={i.nombre} aria-label={`Usar ${i.nombre}`}
                    className={`relative block h-14 w-24 overflow-hidden rounded-lg border-2 ${esta ? "border-violet-600" : "border-transparent hover:border-violet-300"}`}>
                    <img src={String(i.url)} alt="" className="h-full w-full object-cover" loading="lazy" />
                    {esta && <span className="absolute right-1 top-1 rounded-full bg-violet-600 p-0.5 text-white"><Check size={10} /></span>}
                  </button>
                </li>
              );
            })}
          </ul>
          {conLink.length > MAX_A_LA_VISTA && (
            <Link to="/activa/oficial/imagenes" className="text-[11px] font-semibold text-violet-700 hover:underline">Ver las {conLink.length} imágenes</Link>
          )}
        </div>
      ) : null}
      {aviso && <p className="text-[11px] text-amber-700">{aviso}</p>}
    </div>
  );
}

export default ElegirImagen;
